<template>
  <div class="site-header-container">
    <header
      ref="siteHeader"
      class="site-header"
      :class="[checkPersistent, searchActive]"
      data-sign-post-priority="1"
      :js-sign-post="$string('accessibility.sign_posts.site_header')"
    >
      <announcement-bar
        v-if="showAnnouncementBar"
        :announcement-bar="announcementBarObject"
      />

      <div class="site-header__masthead">
        <div class="site-header__container">
          <button
            class="site-header__menu icon-button"
            type="button"
            @click="handleMenuDrawerToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.menu')"
            />

            <span
              class="site-header__hamburger hamburger"
              :class="{ [$class('active')]: getActiveOverlay === 'menuDrawer' }"
            >
              <span class="hamburger__inner" />
            </span>
          </button>

          <a
            class="site-header__logo"
            :href="$variable('routes.root')"
          >
            <icon-logo />

            <component
              :is="titleElement"
              id="site-header-title"
              class="site-header__logo-text sr-only"
            >
              {{ $variable('store.shopName') }}
            </component>
          </a>

          <nav class="site-header__navigation">
            <ul class="site-header__navigation-list">
              <li
                v-for="(tier1) of desktop.items"
                :key="tier1.handle"
                class="site-header__navigation-item"
                :data-handle="tier1.handle"
                @touchstart="handleNavClick($event)"
                @mouseover="setActiveMenu($event)"
                @mouseout="removeActiveMenu($event)"
              >
                <a :href="tier1.url">
                  <button
                    class="site-header__navigation-link"
                    type="button"
                    :class="{ 'active': activeMenu === tier1.handle }"
                  >
                    <span v-text="tier1.title" />
                  </button>
                </a>
              </li>
            </ul>
          </nav>

          <mega-nav
            :active-menu="activeMenu"
            :navigation="desktop.items"
            :signposts="signposts"
            :unbold-column-headers="unboldColumnHeaders"
            @clear-active-menu="clearActiveMenu"
            @enter-mega-menu="handleMegaMenuEnter"
          />

          <div class="site-header__search-account-cart">
            <search-bar
              class="site-header__search-bar"
              mobile-icon
            />

            <currency-modal-trigger />

            <div
              v-if="$variable('customer.enabled')"
              class="site-header__account-wrap"
            >
              <a
                v-if="$variable('customer.loggedIn')"
                class="site-header__account"
                :href="$variable('routes.account.url')"
              >
                <icon-account />

                <span
                  class="sr-only"
                  v-text="$string('general.site_header.account')"
                />
              </a>

              <template v-else>
                <a
                  class="site-header__account"
                  :href="$variable('routes.account.login')"
                >
                  <icon-account />

                  <span
                    class="sr-only"
                    v-text="$string('general.site_header.login')"
                  />
                </a>
              </template>
            </div>

            <a
              class="site-header__cart"
              :href="$variable('routes.cart.url')"
              @click="handleCartToggle"
            >
              <span
                class="visually-hidden"
                v-text="$string('general.site_header.cart')"
              />

              <icon-cart />

              <span
                class="site-header__cart-badge"
                :class="{ 'empty-cart': cart.item_count === 0 }"
                v-text="cart.item_count"
              />
            </a>
          </div>
        </div>
      </div>

      <div
        v-if="showSaleMessage"
        ref="saleMessage"
        class="site-header__sale-message-wrap"
        :style="{ backgroundColor: saleMessage.background }"
      >
        <div class="site-header__sale-message">
          <div
            v-if="saleMessage.header"
            class="site-header__sale-message-header"
            v-text="saleMessage.header"
          />

          <div
            v-if="saleMessage.content"
            class="site-header__sale-message-content"
            v-html="saleMessage.content"
          />

          <btn
            v-if="saleMessage.cta && saleMessage.link"
            class="site-header__sale-message-cta"
            :label="saleMessage.cta"
            custom-element="a"
            :url="saleMessage.link"
            :show-icon="false"
            modifiers="text"
          />
        </div>
      </div>
    </header>

    <overlay
      key="predictiveSearch"
      :block-class="searchOverlayClasses"
      namespace="predictiveSearch"
      :direction="(isMobile ? 'left' : 'down')"
      :show-overlay="(isMobile ? true : false)"
      :type="(isMobile ? 'drawer' : 'shelf')"
      :template="!isMobile"
    >
      <template
        v-if="!isMobile"
        #close
      >
        <icon-close />
      </template>
      <template #body="{ isActive }">
        <predictive-search-bar
          :is-active="isActive"
          :popular-categories="popularCategories"
          include-collections
          include-pages
          include-products
        />
      </template>
    </overlay>

    <overlay
      key="menuDrawer"
      block-class="menu-drawer-overlay"
      direction="right"
      namespace="menuDrawer"
      :template="false"
      type="drawer"
    >
      <template #body="{ isActive }">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          :handle="mobile.handle"
          :is-active="isActive"
          :navigation="mobile.items"
          :shipping-link="shippingLink"
          :signposts="signposts"
        >
          <template #currency>
            <slot name="currency" />
          </template>
        </menu-drawer>

        <div
          v-if="!getComponentActive('menu-drawer')"
          class="site-header__menu-drawer"
        >
          <div
            v-for="(_, index) of [...Array(mobile.itemCount)]"
            :key="`menu-drawer-loading-${index}`"
            class="site-header__menu-drawer-link"
          >
            <span class="site-header__menu-drawer-loading" />
          </div>
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} announcementBar - Announcement bar settings and content.
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {Boolean} persistentTemplate - If page has to have an opaque header.
 * @param {Object} saleMessage - Sale message settings and content.
 * @param {String} shippingLink - URL for shipping & returns link.
 * @param {Object} signposts - Collection of images/link to be shown in submenu.
 * @param {String} titleElement - HTML element of site logo based on page.
 * @param {String} unboldColumnHeaders - Names of any subheadings to unbold.
 *
 */

import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import SearchBar from '~async/search-bar/search-bar'
import AnnouncementBar from '~global/announcement-bar/announcement-bar'
import Btn from '~global/btn/btn'
import CurrencyModalTrigger from '~global/currency-modal-trigger/currency-modal-trigger'
import MegaNav from '~global/mega-nav/mega-nav'
import Overlay from '~global/overlay/overlay'

import IconAccount from '~icons/misc/account.svg'
import IconCart from '~icons/misc/cart.svg'
import IconClose from '~icons/misc/close.svg'
import IconLogo from '~icons/misc/logo.svg'

export default {
  name: 'SiteHeader',

  components: {
    AnnouncementBar,
    Btn,
    CurrencyModalTrigger,
    IconAccount,
    IconCart,
    IconClose,
    IconLogo,
    MegaNav,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    Overlay,
    PredictiveSearchBar: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/predictive-search-bar/predictive-search-bar'
      ),
    }),
    SearchBar,
  },

  props: {
    announcementBar: {
      type: Object,
      default: () => ({}),
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    persistentTemplate: {
      type: Boolean,
      default: false,
    },
    popularCategories: {
      type: Array,
      default: () => [],
    },
    saleMessage: {
      type: Object,
      default: () => ({}),
    },
    shippingLink: {
      type: String,
      default: '',
    },
    signposts: {
      type: Object,
      default: () => ({}),
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
    unboldColumnHeaders: {
      type: String,
      default: '',
    },
    enableStaticNavigationBar: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeMenu: null,
      activeMenuTimeout: null,
      height: {
        total: 0,
        visible: 0,
        noSaleTotal: 0,
        noSaleVisible: 0,
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      headerStateTop: (state) => state.index.scroll.top,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
      settings: (state) => state.index.settings,
    }),

    /**
     * Check if sale message should be shown.
     * @returns {Boolean}
     */
    showSaleMessage() {
      if (
        this.saleMessage.enabled &&
        (
          this.saleMessage.header ||
          this.saleMessage.content ||
          this.saleMessage.cta
        )
      ) {
        return true
      }

      return false
    },

    /**
     * Check if the page is scrolled or not to compute class.
     * @returns {String}
     */
    checkPersistent() {
      if (this.saleMessage.enabled) {
        return 'is-persistent has-sale-message has-sale-message-or-has-static-navigation'
      }

      if (this.enableStaticNavigationBar) {
        return 'is-persistent has-static-navigation has-sale-message-or-has-static-navigation'
      }

      if (this.persistentTemplate) {
        return 'is-persistent'
      }

      if (this.headerStateTop && !this.getActiveOverlay) {
        return ''
      }

      return 'is-persistent'
    },

    /**
     * Compute classes to pass into search overlay.
     * @returns {String}
     */
    searchOverlayClasses() {
      const announcementClass = this.showAnnouncementBar
        ? 'has-announcement-bar'
        : ''
      const searchClass = 'predictive-search-overlay'

      return `${announcementClass} ${searchClass}`
    },

    /**
     * Check if search is active on desktop.
     * @returns {String}
     */
    searchActive() {
      if (this.getActiveOverlay === 'predictiveSearch') {
        return 'search-active'
      }

      return ''
    },

    /**
     * Compute whether to show announcement bar.
     * @returns {Boolean}
     */
    showAnnouncementBar() {
      if (this.announcementBar.enabled && this.announcementBar.announcements.length) {
        return true
      }

      return false
    },

    /**
     * Compute whether to rotate announcement bar, update object if not.
     * @returns {Object}
     */
    announcementBarObject() {
      const object = this.announcementBar

      if (object.announcements.length > 1) {
        return object
      }

      object.enabled = false

      return object
    },
  },

  watch: {

    /**
     * Watch total height to update CSS variables.
     */
    'height.total'() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible'() {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    'height.noSaleTotal'() {
      document.documentElement.style.setProperty(
        '--header-no-sale-height',
        `${this.height.noSaleTotal}px`,
      )
    },

    'height.noSaleVisible'() {
      document.documentElement.style.setProperty(
        '--header-no-sale-visible-height',
        `${this.height.noSaleVisible}px`,
      )
    },
  },

  mounted() {
    window.document.addEventListener('mouseleave', () => this.clearActiveMenu())
    window.addEventListener('resize', () => this.setHeights())

    nextTick().then(() => {
      this.setHeights()
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setStart: 'setStart',
      toggleOverlay: 'overlays/toggle',
      openOverlay: 'overlays/open',
    }),

    /**
     * Set heights of fixed header.
     */
    setHeights() {
      this.height.total = this.$refs.siteHeader.offsetHeight

      /**
       * Visible height is element height combined with translateY value.
       */
      this.height.visible =
        this.$refs.siteHeader.offsetHeight +
        this.$refs.siteHeader.getBoundingClientRect()?.y

      if (this.$refs.saleMessage) {
        const noSaleVisible = this.height.visible - this.$refs.saleMessage.offsetHeight

        this.height.noSaleTotal = this.height.total - this.$refs.saleMessage.offsetHeight
        this.height.noSaleVisible = noSaleVisible ? noSaleVisible : 0
      } else {
        this.height.noSaleTotal = this.height.total
        this.height.noSaleVisible = this.height.visible
      }
    },

    /**
     * Handle menu drawer toggle.
     */
    handleMenuDrawerToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle nav click to allow menu toggle before redirect on tablet.
     * @param {Object} event - Click event.
     */
    handleNavClick(event) {
      if (this.activeMenu === event.currentTarget.dataset.handle) {
        return
      }

      event.preventDefault()
      this.setActiveMenu(event)
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Clear active menu.
     */
    clearActiveMenu() {
      this.activeMenu = null
    },

    /**
     * Set active menu on desktop link mouseenter event.
     * @param {Event} event - Mouse event.
     */
    setActiveMenu(event) {
      clearTimeout(this.activeMenuTimeout)
      this.activeMenu = event.currentTarget.dataset.handle
    },

    /**
     * Remove active menu on desktop link mouseleave event, unless mega nav.
     * @param {Event} event - Mouse event.
     */
    removeActiveMenu(event) {
      const { toElement } = event
      const megaNav = document.querySelector('.mega-nav')

      if (toElement && megaNav.contains(toElement)) {
        return
      }

      this.activeMenuTimeout = setTimeout(() => {
        this.clearActiveMenu()
      }, this.settings.throttle)
    },

    /**
     * Handle interaction when mouseover mega menu.
     */
    handleMegaMenuEnter() {
      clearTimeout(this.activeMenuTimeout)
    },
  },
}
</script>
